import type { ComponentType } from 'react'
import type { SvgIconProps } from '@mui/material/SvgIcon'
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded'
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded'
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded'
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import GpsFixedRoundedIcon from '@mui/icons-material/GpsFixedRounded'
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded'
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded'
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded'
import type { AiOperationTargetRef } from '../../../types/aiOperational'

/**
 * User-facing citation source kinds. Aligned with retrieval scopes plus
 * `user_message` for self-cites that point back to a previous USER turn in
 * the same thread (assistant turns are never cite-able).
 */
export type LineCitationSourceKind =
  | 'timeline'
  | 'checklist'
  | 'meeting'
  | 'upload'
  | 'report'
  | 'automation'
  | 'stock'
  | 'budget'
  | 'governance'
  | 'pulse'
  | 'memory'
  | 'chat_proposal'
  | 'user_message'
  | 'other'

export interface LineCitation {
  /** Stable per-turn identifier ("c1", "c2"…). */
  id: string
  /** AiRetrievalRecord.id, or a synthetic id like `user_turn:<turnId>` for user-message cites. */
  recordId: string
  targetRef?: AiOperationTargetRef
  sourceKind: LineCitationSourceKind
  title: string
  /** 1-2 sentence quote; used as fallback if the live source is deleted. */
  excerpt: string
  confidence: 'high' | 'inferred'
}

/**
 * The raw shape Gemini returns inside its JSON envelope. `handle` matches the
 * [R-N] token embedded in `message`. Post-parse validation resolves this into
 * a full `LineCitation` (or drops the citation + strips the marker).
 */
export interface LineCitationDraft {
  handle: string
  excerpt?: string
  confidence?: 'high' | 'inferred'
}

/** Icon vocabulary — MUI components. Kept adjacent to the type so callers stay in sync. */
export const LINE_CITATION_ICON: Record<LineCitationSourceKind, ComponentType<SvgIconProps>> = {
  timeline: GpsFixedRoundedIcon,
  checklist: AssignmentRoundedIcon,
  meeting: EventRoundedIcon,
  upload: DescriptionRoundedIcon,
  report: BarChartRoundedIcon,
  automation: AutoFixHighRoundedIcon,
  stock: Inventory2RoundedIcon,
  budget: AttachMoneyRoundedIcon,
  governance: ShieldRoundedIcon,
  pulse: GraphicEqRoundedIcon,
  memory: PsychologyRoundedIcon,
  chat_proposal: ChatBubbleOutlineRoundedIcon,
  user_message: ReplyRoundedIcon,
  other: LinkRoundedIcon,
}
